import { FC, useMemo, useState } from 'react';
import { FieldArray, Form, getIn } from 'formik';
// Components
import {
  Box,
  Button,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Loader, Modal } from '../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../styles';
import { IDynamicFieldDetail } from '../../models/dynamic-fields';
import { ILookupType } from '../../models/employee';
import {
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  DndContext,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { OptionListItem } from './OptionListItem';
import { StaticOptionListItem } from './StaticOptionListItem';
import { Close, Save } from '@mui/icons-material';
import { getOrdinalNumber } from '../../helpers';

interface IDynamicFieldsModalContent {
  open: boolean;
  handleClose: () => void;
  setFieldValue: (key: string, val: any) => void;
  values: any;
  touched: any;
  errors: any;
  handleChange: any;
  handleBlur: any;
  handleSubmit: () => void;
  isSubmitting: boolean;
  dirty: boolean;
  isValid: boolean;
  dynamicField: IDynamicFieldDetail | undefined;
  isLoadingInputTypes: boolean;
  inputTypes: ILookupType[] | undefined;
  optionsList: any;
  setOptionsList: any;
  selectedTeam: string;
  selectedType: string;
  isEdit: boolean;
}

export const DyanmicFieldsModalContent: FC<IDynamicFieldsModalContent> = ({
  open,
  handleClose,
  setFieldValue,
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  dirty,
  isValid,
  handleSubmit,
  isSubmitting,
  dynamicField,
  isLoadingInputTypes,
  inputTypes,
  selectedTeam,
  selectedType,
  isEdit,
}) => {
  const classes = useStyles({ isOptionsScroll: values.options.length > 4 });

  const [activeId, setActiveId] = useState<any>();
  const items = useMemo(
    () => values.options.map(({ optionId }: any) => optionId),
    [values.options]
  );
  // Use the state and functions returned from useTable to build your UI

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  function handleDragStart(event: any) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newOptions = arrayMove(values.options, oldIndex, newIndex);
      setFieldValue('options', newOptions);
    }

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const selectedRow = useMemo(() => {
    if (!activeId) {
      return null;
    }
    //@ts-ignore
    const row = values.options.find(option => option?.optionId === activeId) as Row<any>;
    return row;
  }, [activeId, values.options]);

  return (
    <Modal
      maxWidth="md"
      open={open}
      onClose={() => {
        handleClose();
      }}
      title={isEdit ? 'Edit Dynamic Field' : 'Add Dynamic Field'}
    >
      {isSubmitting && <Loader type="overlay" position="centered" />}
      <Fade in={open}>
        <Form onSubmit={handleSubmit} autoComplete="none">
          <Grid className={classes.grid} container spacing={1}>
            <Grid item xs={12} className={classes.grid}>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Type:</span> {selectedType}
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.grid}>
              <Typography className={classes.readonly} variant="body1">
                <span className={classes.emphasis}>Team:</span> {selectedTeam}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <TextField
                  fullWidth
                  variant="outlined"
                  autoComplete="nope"
                  label="Name"
                  name="Name"
                  value={values.name}
                  size="small"
                  onChange={e => {
                    setFieldValue('name', e.target.value);
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  disabled={isLoadingInputTypes}
                  name="inputType"
                  labelId="inputTypeLabel"
                  id="Input Type"
                  value={values.dataType ?? inputTypes?.[0].value}
                  onChange={({ target: { value } }) => {
                    setFieldValue('dataType', value ?? inputTypes?.[0].value);
                    if (value === 1 || value === 2) {
                      if (values.options.length === 0) {
                        setFieldValue('options', [
                          { optionId: 'new-' + new Date().getTime(), text: '' },
                        ]);
                      }
                    }
                  }}
                >
                  {inputTypes &&
                    inputTypes.map((type, index) => {
                      return (
                        <MenuItem key={`${index}`} value={type.value}>
                          {type.description}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
            {(values.dataType === 2 || values.dataType === 1) && (
              <Grid item xs={12} className={classes.optionsContainer}>
                <DndContext
                  sensors={sensors}
                  onDragEnd={handleDragEnd}
                  onDragStart={handleDragStart}
                  onDragCancel={handleDragCancel}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                >
                  <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <FieldArray name="options">
                      {({ push, remove }) => (
                        <>
                          <div className={classes.buttonContainer}>
                            <Button
                              type="button"
                              color="secondary"
                              variant="contained"
                              onClick={() =>
                                push({ optionId: 'new-' + new Date().getTime(), text: '' })
                              }
                            >
                              Add
                            </Button>
                          </div>
                          <div className={classes.list}>
                            {values.options.map((option: any, index: number) => {
                              const text = `options[${index}].text`;
                              const touchedText = getIn(touched, text);
                              const errorText = getIn(errors, text);
                              return (
                                <OptionListItem
                                  key={option.optionId}
                                  item={option}
                                  id={option.optionId}
                                >
                                  <TextField
                                    fullWidth
                                    classes={{ root: classes.textFieldRoot }}
                                    label={`${getOrdinalNumber(index + 1)} option`}
                                    name={text}
                                    value={option.text}
                                    required
                                    helperText={touchedText && errorText ? errorText : ''}
                                    error={Boolean(touchedText && errorText)}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    InputLabelProps={{
                                      classes: { root: classes.inputLabelRoot },
                                    }}
                                    InputProps={{
                                      classes: { root: classes.inputRoot },
                                      endAdornment: values.options.length > 1 && (
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label="remove option"
                                            onClick={() => remove(index)}
                                          >
                                            <Close />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </OptionListItem>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </FieldArray>
                  </SortableContext>
                  <DragOverlay>
                    {activeId && (
                      <StaticOptionListItem item={selectedRow} id={selectedRow?.optionId}>
                        {selectedRow?.text}
                      </StaticOptionListItem>
                    )}
                  </DragOverlay>
                </DndContext>
              </Grid>
            )}
            <Grid container spacing={1} justifyContent={'flex-end'}>
              <Box marginTop="2rem" marginRight="1rem">
                <Button
                  disabled={!dirty || isSubmitting || !isValid}
                  type="submit"
                  startIcon={<Save />}
                  color="secondary"
                  variant="contained"
                >
                  Save
                </Button>
              </Box>
              <Box marginTop="2rem">
                <Button
                  onClick={handleClose}
                  startIcon={<Close />}
                  color="primary"
                  variant="contained"
                >
                  Cancel
                </Button>
              </Box>

              <Grid item xs={12}>
                <Typography variant="caption">
                  Changing Input type may invalidate previously entered selections.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles<Theme, { isOptionsScroll: boolean }>(() => ({
  emphasis: {
    fontWeight: 600,
    color: theme.palette.secondary.main,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  list: {
    paddingRight: ({ isOptionsScroll }) =>
      isOptionsScroll ? theme.spacing(0.5) : theme.spacing(0),
    margin: theme.spacing(0.5, 0, 1, 0),
    maxHeight: 300,
    overflowY: 'auto',
  },
  grid: {
    marginTop: theme.spacing(0.5),
    paddingTop: '0 !important',
  },
  inputRoot: {
    fontSize: '1rem',
  },
  inputLabelRoot: {
    fontSize: '1rem',
  },
  textFieldRoot: {
    fontSize: '1rem',
  },
}));
